import { isDefined } from './guards/isDefined';
import type { LOCALE_CODE } from '~/lib/ContentfulService';
import type { RedirectCacheType } from '~/lib/ContentfulService/ContentfulPageRedirectService';
import { convertContentfulToWebappLocaleCode } from '~/utils/convertContentfulToWebappLocaleCode';
import { convertWebappToContentfulLocaleCode } from '~/utils/convertWebappToContentfulLocaleCode';
import { buildUrlString } from '~/utils/buildUrlString';

/**
 * Extracts the webapp locale code from the URL pathname
 * @param pathname - URL pathname (e.g., "/zh-tw/solutions/...")
 * @returns Lowercase webapp locale or undefined
 */
function extractWebappLocaleFromPath(pathname: string): string | undefined {
  return pathname.split('/').find(Boolean)?.toLowerCase();
}

/**
 * Determines the target contentful locale using priority-based fallback strategy.
 * This matches the server-side behavior in 30.pageRedirect.ts middleware.
 *
 * @param currentWebappLocale - Current webapp locale from URL
 * @param redirectMap - Geo redirect locale to destination slug mapping
 * @param getGeoRedirectLocale - Function to get geo-based locale
 * @returns Target contentful locale
 */
async function determineTargetContentfulLocale(
  currentWebappLocale: string | undefined,
  redirectMap: Record<string, string> | undefined,
  getGeoRedirectLocale?: () => Promise<LOCALE_CODE | undefined>
): Promise<LOCALE_CODE | undefined> {
  // Priority 1: Use current URL locale if it's valid and available in redirect map
  // This matches server-side extractLocaleFromSourcePath behavior
  if (currentWebappLocale && redirectMap) {
    const currentContentfulLocale =
      convertWebappToContentfulLocaleCode(currentWebappLocale);

    // Find matching key with case-insensitive comparison to handle zh-tw -> zh-Hant-TW
    const matchedKey = Object.keys(redirectMap).find(
      (key) => key.toLowerCase() === currentContentfulLocale.toLowerCase()
    );

    if (matchedKey) {
      return matchedKey as LOCALE_CODE;
    }
  }

  // Priority 2: Fallback to geolocation-based locale
  return await getGeoRedirectLocale?.();
}

/**
 * Builds the destination URL string from redirect configuration and current URL.
 *
 * @param redirect - Redirect configuration object
 * @param currentUrl - Current URL object
 * @param getGeoRedirectLocale - Optional function to get geo-based locale
 * @returns Built destination URL string or undefined if invalid
 */
async function buildDestinationUrl(
  redirect: RedirectCacheType,
  currentUrl: URL,
  getGeoRedirectLocale?: () => Promise<LOCALE_CODE | undefined>
): Promise<string | undefined> {
  const { geoRedirectLocaleToDestinationSlugMap: redirectMap } = redirect;

  // Early return if no redirect mapping is available
  if (!redirectMap) {
    return;
  }

  // Extract current webapp locale from URL path
  const currentWebappLocale = extractWebappLocaleFromPath(currentUrl.pathname);

  // Determine target contentful locale using priority-based strategy
  const targetContentfulLocale = await determineTargetContentfulLocale(
    currentWebappLocale,
    redirectMap,
    getGeoRedirectLocale
  );

  // Get destination slug for the target locale (fallback to 'en')
  const destinationSlug = redirectMap[targetContentfulLocale ?? 'en'];

  // Convert contentful locale back to webapp locale for URL building
  const webappLocaleCode = targetContentfulLocale
    ? convertContentfulToWebappLocaleCode(targetContentfulLocale)
    : 'en';

  // Validate that both locale code and slug are available
  if (!webappLocaleCode || !destinationSlug) {
    return;
  }

  return buildUrlString(webappLocaleCode, destinationSlug);
}

export async function createNewUrlFromRedirectAndCurrentUrl(
  redirect: RedirectCacheType,
  currentUrl: URL,
  getGeoRedirectLocale?: () => Promise<LOCALE_CODE | undefined>
): Promise<URL | undefined> {
  let destinationUrl = redirect.destinationUrl;

  if (!isDefined(destinationUrl)) {
    destinationUrl = await buildDestinationUrl(
      redirect,
      currentUrl,
      getGeoRedirectLocale
    );

    // Early return if destination URL could not be built
    if (!destinationUrl) {
      return;
    }
  }

  /**
   * Default url origin is the destinationUrl, fallback is the current origin.
   *
   * Reference https://developer.mozilla.org/en-US/docs/Web/API/URL/URL#parameters
   * > If url is a relative URL, base is required, and will be used as the base URL.
   * If url is an absolute URL, a given base will be ignored.
   */
  // TODO: consider baslerweb.com => baslerweb.cn and vice versa (to prevent another redirect)
  const newUrl = new URL(destinationUrl, currentUrl.origin);

  if (redirect.queryStringForwarding) {
    for (const [key, value] of currentUrl.searchParams.entries()) {
      newUrl.searchParams.append(key, value);
    }
  }

  // TODO: internal site? pathForwarding is senseless!
  if (redirect.pathForwarding) {
    newUrl.pathname = currentUrl.pathname;
  }

  return newUrl;
}
